import { useTransition } from "react"

import {
  createContentView,
  type ImageBlockData,
} from "../../../../core/imageUtils"
import type { MediaAssetOrExternalImage } from "../../../../core/MediaLibrary/hooks/mediaLibraryData"
import type { CroppedImages } from "../../../ImageField/CropperDialog"
import { updateImageMainThumbnail } from "../../../ImageField/CropperDialog/croppedImage"
import { useImageFieldImageUpload } from "../../../ImageField/useImageFieldImageUpload"
import type { ImageViewProps } from "./ImageView"

interface useImageViewProps {
  content: ImageBlockData | undefined
  thumbnail: {
    width: number | undefined
    height: number | undefined
    name: string
  }
  imageViewProps: ImageViewProps
}

export function useImageView(props: useImageViewProps) {
  const {
    content,
    thumbnail,
    imageViewProps: { getPos, updateAttributes, editor, node },
  } = props

  const [isUpdating, startTransition] = useTransition()

  function updateContent(content?: ImageBlockData) {
    startTransition(() => updateAttributes({ content }))
  }

  function onMediaSelected(media: MediaAssetOrExternalImage) {
    const newContent = createContentView(media)
    const endPos = getPos() + node.nodeSize

    updateContent({ ...content, ...newContent })
    editor
      .chain()
      .focus(endPos + 1)
      .run()
  }

  function onCropperDialogClose({
    croppedImages,
  }: {
    croppedImages: CroppedImages
  }) {
    if (!content) return
    const croppedImage = croppedImages[thumbnail.name]

    if (!croppedImage) return

    const newContent = updateImageMainThumbnail({
      content,
      croppedImage,
    })

    if (newContent !== content) updateContent(newContent)
  }

  function onClear() {
    const startPos = getPos()
    const endPos = getPos() + node.nodeSize
    editor
      .chain()
      .deleteRange({ from: startPos, to: endPos })
      .focus(endPos)
      .run()
  }

  const { uploadImage, isUploadingImage } = useImageFieldImageUpload({
    onMediaSelected,
  })

  return {
    onMediaSelected,
    onCropperDialogClose,
    isUpdating,
    updateContent,
    onClear,
    uploadImage,
    isUploadingImage,
  }
}
